import hljs from "highlight.js/lib/core";
import ApplicationController from "../application_component_controller";

import { fetchJSON } from "hb/lib/fetch";
import Query, { DEFAULT_QUERY } from "hb/insights/query";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import { formatShort } from "hb/lib/number_helpers";
import { escapeExpression } from "hb/lib/utils";
import QueryInputHandler from "hb/insights/query_input_handler";
import { extractParameters } from "hb/insights/query_parameters";

export default class extends ApplicationController {
  static targets = [
    "progress",
    "query",
    "preview",
    "resultCounts",
    "error",
    "errorTemplate",
    "pickerSummary",
    "badgerFunctions",
    "queryWrap",
    "shadow",
    "errorHighlights",
  ];

  static values = {
    querySessionId: String,
    query: String,
    widgetId: String,
    outletDataKey: String,
    outletSelector: String,
    badgersenseMetaPath: String,
    alarmMode: Boolean,
    readonly: Boolean,
    parametersEnabled: { type: Boolean, default: false },
  };

  async connect() {
    if (this.readonlyValue) {
      this.readonlyConnect();
    } else {
      this.editConnect();
    }
  }

  editConnect() {
    useListen(this);
    useOutlets(this, {
      components: ["timePicker", "streamSelector"],
      scope: this.element,
    });
    useOutlets(this, { components: ["badgersense"] });

    fetchJSON({ path: this.badgersenseMetaPathValue, method: "GET" }).then(
      (res) => {
        this.badgersenseMeta = res;
        if (this.outletConnected) {
          this.outletComponent("badgersense").badgersenseMeta = res;
        }
      },
    );

    this.classList = this.element.classList;
    this.errorTemplate = this.compileElement(this.errorTemplateTarget);

    this.onQueryStartHandler = this.onQueryStart.bind(this);
    this.onQueryReadyHandler = this.onQueryReady.bind(this);
    this.onQueryErrorHandler = this.onQueryError.bind(this);

    this.listenWindow("queryPush", this.queryPushHandler);
    this.listenWindow("resize", this.repositionBadgersense);
    this.listenWindow("updateQueryState", this.queryStateUpdated);
    this.listen("click", this.onClickOutside, document);

    this.reloadQueryArea();

    this.listen("focus", this.onFocus, this.queryTarget);
    this.listen("blur", this.onBlur, this.queryTarget);
    this.listen("keydown", this.onKeyDown, this.queryTarget);
    this.listen("input", this.onInput, this.queryTarget);
    this.listen("click", this.onSelectionClick, this.queryTarget);

    this.listen(
      "scroll",
      () => {
        this.repositionBadgersense();
      },
      this.queryWrapTarget,
    );

    // listen for resize on queryTarget
    this.queryResizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        this.resizeQueryBox();
        this.repositionBadgersense();
      });
    });
    this.queryResizeObserver.observe(this.queryWrapTarget);

    // Auto mode (no user drag) needs to recompute when the chrome around
    // the textarea changes height — top-bar stacking on narrow viewports,
    // vis-bar stacking, or a horizontal scrollbar appearing inside the
    // query wrap. ResizeObserver on the start-panel doesn't fire for
    // these because the panel's own box hasn't changed yet.
    this.chromeResizeObserver = new ResizeObserver(() => {
      this._lastTextareaScrollHeight = null;
      window.requestAnimationFrame(() => this.resizeQueryBox());
    });
    for (const el of this.element.querySelectorAll(".top-bar, .vis-bar")) {
      this.chromeResizeObserver.observe(el);
    }

    // When the QueryComponent is nested inside a vertical PanelsComponent,
    // observe the start-panel so the textarea follows manual drag-resize.
    const verticalPanels = this.element.closest(
      "[data-panels-component-orientation-value='vertical']",
    );
    const verticalStartPanel = verticalPanels?.querySelector(
      ":scope > .start-panel",
    );
    if (verticalStartPanel) {
      this.panelResizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => this.resizeQueryBox());
      });
      this.panelResizeObserver.observe(verticalStartPanel);

      // Dblclick on the resizer clears the user's drag-set cap and
      // emits `panels:autoSize`. Invalidate our scrollHeight cache so
      // the next resizeQueryBox treats this as a content change and
      // shrinks the panel back to content height (under the 280px
      // auto-grow cap).
      this.listen(
        "panels:autoSize",
        () => {
          this._lastTextareaScrollHeight = null;
          window.requestAnimationFrame(() => this.resizeQueryBox());
        },
        verticalPanels,
      );
    }

    Query.on("start", this.querySessionIdValue, this.onQueryStartHandler);
    Query.on("ready", this.querySessionIdValue, this.onQueryReadyHandler);
    Query.on("error", this.querySessionIdValue, this.onQueryErrorHandler);

    this.timezone = null;
    this.queryInputHandler = new QueryInputHandler();

    this.queryTarget.value = this.queryValue || DEFAULT_QUERY;
    this.reloadQueryArea();

    // The server renders the query into the textarea, so the caret starts at
    // 0. Undoing back to the opening query should leave it at the end.
    this.queryInputHandler.addUndoState(
      this.queryTarget.value,
      this.queryTarget.value.length,
    );
    this.suggesting = false;
  }

  readonlyConnect() {
    this.resizeQueryBox();
    this.previewTarget.innerHTML = hljs.highlight(this.queryTarget.value, {
      language: "badgerql",
    }).value;
    this.element.classList.add("highlighted");
  }

  disconnect() {
    if (!this.readonlyValue) {
      this.editDisconnect();
    }
  }

  editDisconnect() {
    this.queryResizeObserver.disconnect();
    this.panelResizeObserver?.disconnect();
    this.chromeResizeObserver?.disconnect();

    Query.off("start", this.querySessionIdValue, this.onQueryStartHandler);
    Query.off("ready", this.querySessionIdValue, this.onQueryReadyHandler);
    Query.off("error", this.querySessionIdValue, this.onQueryErrorHandler);
  }

  get badgersenseOutlet() {
    return this.outletComponent("badgersense");
  }

  hasSuggestions() {
    this.suggesting = true;
  }

  queryPushHandler({ query, focus = true }) {
    const index = query.indexOf("???");
    if (index !== -1) {
      query = query.replace("???", "");
    }

    this.queryTarget.value = query;
    if (index !== -1) {
      this.queryTarget.selectionStart = this.queryTarget.selectionEnd = index;
    }
    this.recordUndoState();
    this.reloadQueryArea();
    if (focus) this.queryTarget.focus();
  }

  // Programmatic replacements of the query need a spot in the undo history
  // too, or the next undo skips straight past them.
  recordUndoState() {
    this.queryInputHandler.addUndoState(
      this.queryTarget.value,
      this.queryTarget.selectionStart,
    );
  }

  outletsConnected() {
    if (this.readonlyValue) return;
    if (!this.badgersenseOutlet) return;

    this.badgersenseOutlet.badgersenseMeta = this.badgersenseMeta;
    this.badgersenseOutlet.setupCallbacks({
      onSuggestionSelect: this.onSuggestionSelect.bind(this),
      positions: this.positions.bind(this),
      hasSuggestions: this.hasSuggestions.bind(this),
      onDocLinkSelect: this.docLinkSelect.bind(this),
    });
  }

  queryStateUpdated({ query }) {
    if (query !== this.queryTarget.value) {
      this.queryTarget.value = query;
      this.recordUndoState();
      this.reloadQueryArea();
      this.onQueryChange();
    }
  }

  onQueryStart() {
    const hadError = this.classList.contains("query-error");
    this.errorTarget.replaceChildren();
    this.classList.remove("query-complete", "query-error");
    this.classList.add("query-running");
    if (hadError) this._refitForError();
  }

  onQueryReady({
    detail: {
      query: { data },
    },
  }) {
    this.queryId = null;
    this.classList.remove("query-running");
    this.classList.add("query-complete");

    const { rowCount, totalCount } = data.meta;

    const parts = [`<span>${rowCount}</span>`];

    if (totalCount !== null) {
      parts.push(" of ", `<span>${formatShort(totalCount)}</span>`);
    }

    parts.push("<div>results</div>");

    this.resultCountsTarget.innerHTML = parts.join("\n");
  }

  onQueryError({
    detail: {
      query: { data },
    },
  }) {
    const { error } = data.response;

    this.queryId = null;
    this.classList.add("query-error");
    this.classList.remove("query-running");

    this.errorTarget.innerHTML = this.errorTemplate({
      error: error.message,
    });
    this._refitForError();

    // Skip error position markers for parameterized queries — offsets from
    // Opticon are relative to the resolved query, not the raw query shown
    // in the editor, so they would highlight the wrong position.
    if (
      !this.parametersEnabledValue ||
      extractParameters(this.queryTarget.value).length === 0
    ) {
      if (Array.isArray(error?.range)) {
        this.highlightErrorRange(error?.range[0], error?.range[1]);
      } else if (Number.isInteger(error?.range)) {
        this.highlightErrorSpot(error?.range);
      }
    }
  }

  // The error message lives inside the start panel but doesn't change the
  // panel's own dimensions, so ResizeObserver won't fire. Invalidate the
  // textarea-height cache and re-run resizeQueryBox so the chrome calc
  // picks up (or drops) the error message height.
  _refitForError() {
    this._lastTextareaScrollHeight = null;
    requestAnimationFrame(() => this.resizeQueryBox());
  }

  checkDirty() {
    if (this.queryTarget.value !== this.query) {
      this.classList.add("dirty-query");
    } else {
      this.classList.remove("dirty-query");
    }
  }

  repositionBadgersense() {
    this.outletComponent("badgersense")?.reposition();
  }

  //
  // To allow for scrolling with a syntax highlight overlay, if the textarea is
  // beyond the parent we resize the textarea on change to extend past the
  // parent div.
  //
  // When the query lives inside the vertical PanelsComponent split, we also
  // grow the top panel along with the textarea up to MAX_AUTO_PANEL_PX.
  // Beyond that the textarea scrolls internally and the user can drag the
  // resizer to make the panel taller.
  //
  resizeQueryBox() {
    const MAX_AUTO_PANEL_PX = 280;

    const panels = this.element.closest(
      "[data-panels-component-orientation-value='vertical']",
    );
    const startPanel = panels?.querySelector(":scope > .start-panel");

    // During a drag, follow the panel using the chrome value cached from
    // the most recent non-drag pass. The textarea height is then a pure
    // linear function of panel size — no per-frame layout reads, so no
    // 1px jitter at the scrollbar threshold. Using the same chrome value
    // the non-drag path computes also means the release recompute lands
    // on the same number — no visible pop on release.
    if (panels?.dataset.dragging === "true") {
      const chrome = this._lastChrome ?? 0;
      const errorH = this._lastErrorHeight ?? 0;
      const natural = this._lastTextareaScrollHeight || 0;
      const target = Math.max(
        natural,
        startPanel.offsetHeight - chrome - errorH,
      );
      this.queryTarget.style.height = `${target}px`;
      return;
    }

    this.queryTarget.style.height = "auto";
    const lineHeight =
      parseInt(getComputedStyle(this.queryTarget).lineHeight, 10) || 20;
    const paddingTop =
      parseInt(getComputedStyle(this.queryTarget).paddingTop, 10) || 0;
    const paddingBottom =
      parseInt(getComputedStyle(this.queryTarget).paddingBottom, 10) || 0;
    const naturalTextareaHeight = Math.max(
      this.queryTarget.scrollHeight,
      lineHeight + paddingTop + paddingBottom,
    );

    this.queryTarget.style.width = "auto";
    const queryScrollWidth = this.queryTarget.scrollWidth;
    if (queryScrollWidth > this.queryWrapTarget.offsetWidth) {
      this.queryTarget.style.width = `${queryScrollWidth}px`;
    } else {
      this.queryTarget.style.width = `100%`;
    }

    if (!panels || !startPanel) {
      this.queryTarget.style.height = `${naturalTextareaHeight}px`;
      return;
    }

    // Compute chrome (the non-textarea panel content) without the error
    // message so the base panel size is capped by user drag / max-auto.
    // The error gets added on top — it should always be fully visible
    // even if the user dragged the panel small.
    let chrome = 0;
    let errorHeight = 0;
    for (const child of startPanel.children) {
      if (child === this.element) {
        for (const inner of this.element.querySelectorAll(
          ".top-bar, .vis-bar",
        )) {
          chrome += inner.offsetHeight;
        }
        errorHeight = this.errorTarget.offsetHeight;
      } else {
        chrome += child.offsetHeight;
      }
    }
    // If the query line overflows horizontally, the query-wrap shows a
    // horizontal scrollbar that eats into the textarea's visible area.
    // Add its height to the chrome so the panel allocates room for it.
    if (this.queryWrapTarget.scrollWidth > this.queryWrapTarget.clientWidth) {
      chrome +=
        this.queryWrapTarget.offsetHeight - this.queryWrapTarget.clientHeight;
    }

    const userDragSize = parseInt(panels.dataset.userDragSize || "0", 10);
    const naturalContent = chrome + naturalTextareaHeight;

    // Once the user drags the resizer we treat their chosen height as the
    // source of truth — text changes don't snap the panel back to content
    // height. Dblclick clears userDragSize and we fall back to auto.
    //
    // On the first call, suppress the recompute if the inline FOUC
    // script restored a panel size from localStorage — we want to
    // honor that restored size instead of immediately collapsing to
    // content height.
    const isFirstCall = !this._panelInitialized;
    this._panelInitialized = true;
    const restoredFromStorage =
      isFirstCall && panels.style.getPropertyValue("--start-panel-size") !== "";
    const contentChanged =
      !restoredFromStorage &&
      naturalTextareaHeight !== this._lastTextareaScrollHeight;
    this._lastTextareaScrollHeight = naturalTextareaHeight;
    const chromeChanged = chrome !== (this._lastChrome ?? chrome);
    this._lastChrome = chrome;
    const errorDelta = errorHeight - (this._lastErrorHeight || 0);
    this._lastErrorHeight = errorHeight;

    let panelSize;
    if (userDragSize > 0) {
      // Drag mode: keep the user's height. Adjust only for error appearing
      // or disappearing so the alert is always visible.
      const current = parseInt(
        getComputedStyle(panels).getPropertyValue("--start-panel-size"),
        10,
      );
      if (errorDelta !== 0 && Number.isFinite(current)) {
        panelSize = current + errorDelta;
        panels.style.setProperty("--start-panel-size", `${panelSize}px`);
      } else {
        panelSize = startPanel.offsetHeight;
      }
    } else if (
      contentChanged ||
      chromeChanged ||
      (isFirstCall && !restoredFromStorage) ||
      errorDelta !== 0
    ) {
      // Auto mode: size to content (capped) plus any visible error.
      panelSize = Math.min(naturalContent, MAX_AUTO_PANEL_PX) + errorHeight;
      panels.style.setProperty("--start-panel-size", `${panelSize}px`);
    } else {
      panelSize = startPanel.offsetHeight;
    }

    const avail = panelSize - chrome - errorHeight;
    this.queryTarget.style.height = `${Math.max(naturalTextareaHeight, avail)}px`;
  }

  openDocLink(evt) {
    this.docLinkSelect(evt.params.docid);
  }

  docLinkSelect(linkId) {
    window.dispatchEvent(
      new CustomEvent("doclink", {
        detail: { link: linkId, smooth: false },
      }),
    );
  }

  closeDocs() {
    window.dispatchEvent(new CustomEvent("closedocs", {}));
  }

  onSuggestionSelect(suggestion) {
    let ret = {};
    this.queryInputHandler.insertFromSuggestion(
      ret,
      {
        query: this.queryTarget.value,
        start: this.queryTarget.selectionStart,
        selection: this.badgersenseOutlet.currentSelection,
      },
      suggestion,
    );
    this.queryTarget.value = ret.query;
    this.queryTarget.selectionStart = this.queryTarget.selectionEnd =
      ret.cursorPos;
    this.queryInputHandler.addUndoState(
      ret.query,
      this.queryTarget.selectionStart,
    );
    this.reloadQueryArea();
    this.updateSuggestions(true);
    this.queryTarget.focus();
  }

  removeSuggestions() {
    this.suggesting = false;
    this.badgersenseOutlet.clear();
  }

  removeErrorHighlights() {
    this.errorHighlightsTarget.innerHTML = "";
  }

  highlightErrorRange(start, end) {
    const { value: query } = this.queryTarget;
    const begin = query.slice(0, start);
    const squiggle = query.slice(start, end);
    const rest = query.slice(end);
    this.errorHighlightsTarget.innerHTML =
      escapeExpression(begin) +
      '<span class="error-fragment">' +
      escapeExpression(squiggle) +
      "</span>" +
      escapeExpression(rest);
  }

  highlightErrorSpot(offset) {
    const { value: query } = this.queryTarget;
    const begin = query.slice(0, offset);
    const marker = query.slice(offset, offset + 1);
    const rest = query.slice(offset);
    this.errorHighlightsTarget.innerHTML =
      escapeExpression(begin) +
      '<span class="error-marker-wrap"><span class="error-marker">' +
      escapeExpression(marker) +
      "</span></span>" +
      escapeExpression(rest);
  }

  positions(offset) {
    const { value: query } = this.queryTarget;
    if (offset === 0) {
      this.shadowTarget.innerHTML =
        '<span class="shadow-offset">.</span>' + escapeExpression(query);
    } else {
      const start = query.slice(0, offset);
      const rest = query.slice(offset);
      this.shadowTarget.innerHTML =
        escapeExpression(start) +
        '<span class="shadow-offset">.</span>' +
        escapeExpression(rest);
    }

    const offsetEl = this.shadowTarget.querySelector(".shadow-offset");
    const rect = offsetEl.getBoundingClientRect();

    const { scrollLeft, offsetWidth } = this.queryWrapTarget;

    let res = {
      left: rect.left,
      bottom: rect.bottom + 5 + window.pageYOffset,
      width: offsetWidth - offsetEl.offsetLeft + scrollLeft + 35,
    };

    if (res.width < 300) {
      res.left -= 300 - res.width;
      res.width = 300;
    }

    return res;
  }

  submitQuery() {
    this.emitWindow("submitQuery", {
      query: this.queryTarget.value,
      querySessionId: this.querySessionIdValue,
    });
  }

  classifyPosition(fromInput = false) {
    const { value: query, selectionStart, selectionEnd } = this.queryTarget;
    this.removeSuggestions();

    if (selectionStart !== selectionEnd) return;

    this.repositionBadgersense();
    this.badgersenseOutlet.suggest(query, selectionStart, fromInput);
  }

  updateSuggestions(fromInput = false) {
    setTimeout(() => {
      this.classifyPosition(fromInput);
    }, 0);
  }

  onClickOutside({ target }) {
    // This prevents the dropdown from closing when you click the
    // first time
    if (this.firstInputClick === true) return;

    const inside = [this.outletElement("badgersense"), this.queryTarget].find(
      (t) => {
        return t.contains(target);
      },
    );

    if (inside) return;

    this.removeSuggestions();
    this.firstInputClick = undefined;
  }

  onBlur() {
    this.firstInputClick = undefined;
  }

  onFocus() {
    if (this.firstInputClick === undefined) {
      this.firstInputClick = true;
      this.updateSuggestions();
    }
  }

  get shouldSuggest() {
    return this.suggesting && this.badgersenseOutlet.hasSuggestions;
  }

  onSelectionClick() {
    // If we have already clicked once, don't do anything
    if (this.firstInputClick === true) {
      this.firstInputClick = false;
      return;
    }
    this.updateSuggestions(true);
  }

  onInput(evt) {
    this.queryInputHandler.addUndoState(
      evt.target.value,
      evt.target.selectionStart,
    );
    this.updateSuggestions(true);
    this.reloadQueryArea();
    this.onQueryChange();
  }

  onQueryChange() {
    this.removeErrorHighlights();
    this.emit("queryChange", { query: this.queryTarget.value });
  }

  reloadQueryArea(el = this.queryTarget) {
    this.resizeQueryBox();
    this.repositionBadgersense();
    this.checkDirty();
    this.previewTarget.innerHTML = hljs.highlight(el.value, {
      language: "badgerql",
    }).value;
    this.classList.add("highlighted");
  }

  moveSelectionUp() {
    this.badgersenseOutlet.moveSelection(-1);
  }

  moveSelectionDown() {
    this.badgersenseOutlet.moveSelection(1);
  }

  onKeyDown(event) {
    const { target, key, metaKey, ctrlKey, altKey, shiftKey } = event;
    const ret = this.queryInputHandler.handle({
      key,
      metaKey,
      ctrlKey,
      altKey,
      shiftKey,
      badgersenseMeta: this.badgersenseMeta,
      query: target.value,
      selection: this.badgersenseOutlet.currentSelection,
      // Putting in a thunk since it does some DOM stuff
      getSuggestion: () => this.badgersenseOutlet.currentSuggestion,
      isSuggesting: this.suggesting,
      start: target.selectionStart,
      end: target.selectionEnd,
    });

    if (ret === null) return;

    if (ret.preventDefault) event.preventDefault();
    if (ret.query !== undefined) target.value = ret.query;
    if (ret.cursorPos !== undefined) {
      target.selectionStart = target.selectionEnd = ret.cursorPos;
    }
    ret.calls.forEach((fn) => {
      if (Array.isArray(fn)) {
        const [name, ...args] = fn;
        this[name].call(this, ...args);
      } else {
        this[fn].call(this);
      }
    });
  }
}
